import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Smartphone } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { ALL_MENU_ITEMS } from '../components/AdminLayout';

const SKIP_TOP_LEVEL = new Set(['conversations', 'dashboard', 'orders', 'sitemap']);

const AdminMore: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<{ role?: string } | null>(null);
  const [installState, setInstallState] = useState<'idle' | 'ready' | 'installed'>('idle');

  // PWA 安裝狀態：已安裝（standalone）／可安裝（捕獲到 beforeinstallprompt）／iOS 教學
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone === true;
    if (standalone) { setInstallState('installed'); return; }
    if ((window as any).__crmInstallPrompt) setInstallState('ready');
    const onReady = () => setInstallState('ready');
    window.addEventListener('crm-installable', onReady);
    return () => window.removeEventListener('crm-installable', onReady);
  }, []);

  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

  const handleInstall = async () => {
    const prompt = (window as any).__crmInstallPrompt;
    if (!prompt) return;
    prompt.prompt();
    await prompt.userChoice;
    setInstallState(window.matchMedia('(display-mode: standalone)').matches ? 'installed' : 'idle');
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    const stored = localStorage.getItem('admin_user');
    if (stored) setUser(JSON.parse(stored));
  }, [navigate]);

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) { localStorage.removeItem('admin_token'); navigate('/admin'); }
  };

  const role = user?.role || '';
  const visible = ALL_MENU_ITEMS.filter((m) => {
    if (SKIP_TOP_LEVEL.has(m.id) || m.id === 'settings') return false;
    if (m.children) return true; // 分組一定有內容；子項角色過濾在卡片 render 時做
    if (m.roles && !m.roles.includes(role as never)) return false;
    return true;
  });

  const resolveChildren = (m: typeof ALL_MENU_ITEMS[number]) => {
    if (!m.children) {
      return [{ label: m.label, icon: m.icon, path: m.path, roles: m.roles }];
    }
    return m.children.filter((c) => !c.roles || c.roles.includes(role as never));
  };

  return (
    <AdminLayout currentPage="more" onLogout={handleLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">更多功能</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">全部後台功能分組入口。</p>
        </div>

        {/* 📲 PWA 安裝卡（僅管理員登入後可見——admin-scoped PWA） */}
        {installState === 'ready' && (
          <button onClick={handleInstall} className="flex w-full items-center justify-between rounded-xl border border-red-200 bg-red-50 p-4 text-left transition hover:border-red-400 dark:border-red-800 dark:bg-red-900/20">
            <span>
              <span className="block font-bold text-red-700 dark:text-red-300">📲 安裝辣谷 CRM App</span>
              <span className="block text-xs text-red-600/80 dark:text-red-400/80">一撣即裝——手機主畫面／桌面獨立視窗，打開就係對話收件箱</span>
            </span>
            <Smartphone size={22} className="shrink-0 text-red-600" />
          </button>
        )}
        {installState === 'idle' && isIOS && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
            <p className="font-bold text-amber-700 dark:text-amber-300">📲 安裝辣谷 CRM（iOS）</p>
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Safari 底部「分享」→ 揀「加入主畫面」→ 確認。之後由主畫面圖示打開就係 App 模式。</p>
          </div>
        )}
        {installState === 'installed' && (
          <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300">
            ✓ 已以 App 模式運行
          </div>
        )}

        {visible.map((m) => {
          const children = resolveChildren(m).filter((c) => !(c as { path?: string }).path?.includes(':'));
          if (children.length === 0) return null;
          const Icon = m.icon;
          return (
            <section key={m.id} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-gray-500 dark:text-gray-400">
                <Icon size={16} className="text-red-600" /> {m.label}
              </h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {children.map((c) => {
                  const CIcon = c.icon;
                  return (
                    <button
                      key={c.path}
                      onClick={() => navigate((c as { path: string }).path)}
                      className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-3 text-left text-sm font-medium text-gray-700 transition hover:border-red-400 hover:text-red-600 dark:border-gray-600 dark:text-gray-200 dark:hover:border-red-500"
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        {CIcon && <CIcon size={16} className="shrink-0 text-red-500" />}
                        <span className="truncate">{c.label}</span>
                      </span>
                      <ChevronRight size={14} className="shrink-0 text-gray-400" />
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </AdminLayout>
  );
};

export default AdminMore;
